import { useState } from "react";

const pathways = [
  {
    id: 1,
    badge: "Pathway 1",
    headline: "Build Your Foundation",
    audience:
      "For fresh candidates with limited industry or subject matter knowledge",
    description:
      "The book provides a comprehensive foundation, while the subscription delivers instructor-led video tutorials, mock exams, and the question bank for active practice and exam readiness.",
    components: [
      {
        label: "Amazon Book",
        price: "CAD $299.99",
        detail: "Comprehensive Derivatives study guide",
        icon: "📘",
      },
      {
        label: "Monthly Subscription",
        price: "$29.99/month",
        detail: "Video tutorials · Mock exams · Question bank",
        icon: "🎓",
      },
    ],
    accent: "#92400e",
    accentLight: "#fef3c7",
    accentMid: "#fcd34d",
  },
  {
    id: 2,
    badge: "Pathway 2",
    headline: "Accelerate Your Prep",
    audience:
      "For candidates with a finance degree or relevant industry experience",
    description:
      "The interactive quick study guide delivers a focused review of core concepts, while the subscription provides video tutorials, mock exams, and the question bank to sharpen your exam readiness.",
    components: [
      {
        label: "Online Interactive Quick Study Guide",
        price: "CAD $49.99",
        detail: "Focused concept review",
        icon: "⚡",
      },
      {
        label: "Monthly Subscription",
        price: "$29.99/month",
        detail: "Video tutorials · Mock exams · Question bank",
        icon: "🎓",
      },
    ],
    accent: "#92400e",
    accentLight: "#fef3c7",
    accentMid: "#fcd34d",
  },
];

export default function VittaDerivativesPathways() {
  const [hoveredCard, setHoveredCard] = useState(null);

  return (
    <div style={styles.wrapper}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;700;800&family=Source+Sans+3:wght@300;400;500;600;700&display=swap');
        .pw-card { transition: transform 0.35s cubic-bezier(0.22,1,0.36,1), box-shadow 0.35s cubic-bezier(0.22,1,0.36,1); }
        .pw-card:hover { transform: translateY(-6px); }
        .comp-row { transition: background-color 0.2s ease, transform 0.2s ease; }
        .comp-row:hover { transform: translateX(4px); }
        .pw-badge { animation: fadeIn 0.5s ease forwards; opacity: 0; }
        @keyframes fadeIn { from { opacity:0; transform:translateY(-8px); } to { opacity:1; transform:translateY(0); } }
        @keyframes pulse { 0%,100%{ opacity:0.45; } 50%{ opacity:0.7; } }
      `}</style>

      <div style={styles.headerBlock}>
        <div style={styles.examBadge}>DERIVATIVES</div>
        <p style={styles.overline}>DERIVATIVES FUNDAMENTALS &amp; OPTIONS LICENSING</p>
        <h2 style={styles.sectionTitle}>Choose Your Study Pathway</h2>
        <p style={styles.sectionSubtitle}>
          Select the pathway that matches your background — both lead to full
          Derivatives exam readiness.
        </p>
      </div>

      <div style={styles.grid}>
        {pathways.map((pw) => {
          const isHovered = hoveredCard === pw.id;
          return (
            <div
              key={pw.id}
              className="pw-card"
              onMouseEnter={() => setHoveredCard(pw.id)}
              onMouseLeave={() => setHoveredCard(null)}
              style={{
                ...styles.card,
                boxShadow: isHovered
                  ? `0 20px 60px -12px ${pw.accent}22, 0 8px 24px -8px rgba(0,0,0,0.08)`
                  : "0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.04)",
                borderColor: isHovered ? pw.accentMid : "#e2e8f0",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  width: 120,
                  height: 120,
                  background: `radial-gradient(circle at top right, ${pw.accentLight}, transparent 70%)`,
                  borderRadius: "0 16px 0 0",
                  animation: isHovered ? "pulse 2.5s ease infinite" : "none",
                  opacity: 0.5,
                  pointerEvents: "none",
                }}
              />

              <div
                className="pw-badge"
                style={{ ...styles.badge, backgroundColor: pw.accent }}
              >
                {pw.badge}
              </div>

              <h3 style={styles.cardHeadline}>{pw.headline}</h3>

              <div
                style={{
                  ...styles.audienceTag,
                  backgroundColor: pw.accentLight,
                  color: pw.accent,
                  borderLeft: `3px solid ${pw.accent}`,
                }}
              >
                {pw.audience}
              </div>

              <p style={styles.cardDescription}>{pw.description}</p>

              <div style={styles.divider}>
                <div
                  style={{
                    ...styles.dividerLine,
                    background: `linear-gradient(90deg, ${pw.accent}30, transparent)`,
                  }}
                />
                <span style={{ ...styles.dividerLabel, color: pw.accent }}>
                  INCLUDES
                </span>
                <div
                  style={{
                    ...styles.dividerLine,
                    background: `linear-gradient(90deg, transparent, ${pw.accent}30)`,
                  }}
                />
              </div>

              <div style={styles.componentsList}>
                {pw.components.map((comp, idx) => (
                  <div key={idx}>
                    <div
                      className="comp-row"
                      style={{
                        ...styles.componentRow,
                        backgroundColor:
                          idx % 2 === 0 ? `${pw.accentLight}88` : "transparent",
                      }}
                    >
                      <div style={styles.componentIcon}>{comp.icon}</div>
                      <div style={styles.componentInfo}>
                        <div style={styles.componentLabelRow}>
                          <span style={styles.componentLabel}>{comp.label}</span>
                          {comp.price && (
                            <span
                              style={{
                                ...styles.priceTag,
                                backgroundColor: pw.accent,
                              }}
                            >
                              {comp.price}
                            </span>
                          )}
                        </div>
                        <span style={styles.componentDetail}>{comp.detail}</span>
                      </div>
                    </div>
                    {idx < pw.components.length - 1 && (
                      <div style={styles.plusConnector}>
                        <div
                          style={{
                            ...styles.plusIcon,
                            backgroundColor: pw.accentLight,
                            color: pw.accent,
                            border: `1.5px solid ${pw.accent}40`,
                          }}
                        >
                          +
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const styles = {
  wrapper: {
    fontFamily: "'Source Sans 3', sans-serif",
    maxWidth: 960,
    margin: "0 auto",
    padding: "56px 24px",
    backgroundColor: "#f8fafc",
  },
  headerBlock: { textAlign: "center", marginBottom: 48 },
  examBadge: {
    display: "inline-block",
    fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: 14,
    fontWeight: 800,
    letterSpacing: "0.18em",
    color: "#92400e",
    border: "2px solid #92400e",
    borderRadius: 8,
    padding: "6px 20px",
    marginBottom: 14,
  },
  overline: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.12em",
    color: "#64748b",
    marginBottom: 10,
    textTransform: "uppercase",
  },
  sectionTitle: {
    fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: 34,
    fontWeight: 700,
    color: "#0f172a",
    margin: "0 0 12px",
    lineHeight: 1.2,
  },
  sectionSubtitle: {
    fontSize: 15,
    color: "#64748b",
    margin: 0,
    fontWeight: 400,
    maxWidth: 520,
    marginLeft: "auto",
    marginRight: "auto",
    lineHeight: 1.55,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 32,
    alignItems: "start",
  },
  card: {
    position: "relative",
    overflow: "hidden",
    backgroundColor: "#ffffff",
    borderRadius: 16,
    border: "1.5px solid #e2e8f0",
    padding: "36px 32px 32px",
    cursor: "default",
  },
  badge: {
    display: "inline-block",
    color: "#ffffff",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    padding: "5px 14px",
    borderRadius: 20,
    marginBottom: 18,
  },
  cardHeadline: {
    fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: 26,
    fontWeight: 700,
    color: "#0f172a",
    margin: "0 0 14px",
    lineHeight: 1.25,
  },
  audienceTag: {
    fontSize: 13.5,
    fontWeight: 600,
    padding: "10px 16px",
    borderRadius: "0 10px 10px 0",
    marginBottom: 16,
    lineHeight: 1.45,
  },
  cardDescription: {
    fontSize: 14.5,
    color: "#475569",
    lineHeight: 1.65,
    margin: "0 0 22px",
  },
  divider: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 20,
  },
  dividerLine: { flex: 1, height: 1 },
  dividerLabel: { fontSize: 10, fontWeight: 700, letterSpacing: "0.14em" },
  componentsList: { display: "flex", flexDirection: "column", gap: 0 },
  componentRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 14,
    padding: "14px 16px",
    borderRadius: 12,
  },
  componentIcon: { fontSize: 24, lineHeight: 1, flexShrink: 0, marginTop: 2 },
  componentInfo: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    flex: 1,
  },
  componentLabelRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },
  componentLabel: { fontSize: 15, fontWeight: 700, color: "#0f172a" },
  priceTag: {
    fontSize: 11,
    fontWeight: 700,
    color: "#ffffff",
    padding: "3px 10px",
    borderRadius: 6,
    letterSpacing: "0.02em",
    whiteSpace: "nowrap",
  },
  componentDetail: { fontSize: 13, color: "#64748b", fontWeight: 400 },
  plusConnector: { display: "flex", justifyContent: "center", padding: "4px 0" },
  plusIcon: {
    width: 26,
    height: 26,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    fontWeight: 700,
  },
};
